// ═══════════════════════════════════════════════════════════
// AUDIO SYSTEM - Retro Chiptune Sounds
// ═══════════════════════════════════════════════════════════

export class AudioManager {
    private context: AudioContext | null = null;
    private masterGain: GainNode | null = null;
    private muted: boolean = false;
    private musicOscillators: OscillatorNode[] = [];
    private musicPlaying: boolean = false;

    constructor() {
        this.initAudio();
    }

    private initAudio(): void {
        try {
            this.context = new (window.AudioContext || (window as any).webkitAudioContext)();
            this.masterGain = this.context.createGain();
            this.masterGain.connect(this.context.destination);
            this.masterGain.gain.value = 0.3; // Master volume
        } catch (e) {
            console.warn('Web Audio API not supported', e);
        }
    }

    private playTone(frequency: number, duration: number, type: OscillatorType = 'square', volume: number = 0.3): void {
        if (!this.context || !this.masterGain || this.muted) return;

        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.type = type;
        oscillator.frequency.value = frequency;

        gainNode.gain.value = volume;
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + duration);

        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + duration);
    }

    playCornerHit(): void {
        if (!this.context || this.muted) return;

        // Arpeggio upward - very satisfying!
        const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.playTone(freq, 0.1, 'square', 0.4);
            }, i * 40);
        });

        // Bass hit
        this.playTone(130.81, 0.15, 'sine', 0.3);
    }

    playBounce(): void {
        if (!this.context || this.muted) return;

        // Short blip
        this.playTone(200, 0.05, 'square', 0.15);
    }

    playLaunch(): void {
        if (!this.context || this.muted) return;

        // Whoosh sound - sweeping frequency for launch
        if (!this.context || !this.masterGain) return;

        const oscillator = this.context.createOscillator();
        const gainNode = this.context.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(this.masterGain);

        oscillator.type = 'sawtooth';

        // Sweep from high to low frequency
        oscillator.frequency.setValueAtTime(800, this.context.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(200, this.context.currentTime + 0.15);

        gainNode.gain.value = 0.25;
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.context.currentTime + 0.15);

        oscillator.start(this.context.currentTime);
        oscillator.stop(this.context.currentTime + 0.15);
    }

    playGameOver(): void {
        if (!this.context || this.muted) return;

        // Descending sad trombone
        const notes = [392, 349.23, 329.63, 293.66]; // G4, F4, E4, D4
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.playTone(freq, 0.3, 'triangle', 0.3);
            }, i * 150);
        });
    }

    playStart(): void {
        if (!this.context || this.muted) return;

        // Ascending startup sound
        const notes = [261.63, 329.63, 392, 523.25]; // C4, E4, G4, C5
        notes.forEach((freq, i) => {
            setTimeout(() => {
                this.playTone(freq, 0.1, 'square', 0.3);
            }, i * 60);
        });
    }

    playTick(): void {
        if (!this.context || this.muted) return;

        // Short tick for countdown
        this.playTone(880, 0.05, 'square', 0.1);
    }

    playWarning(): void {
        if (!this.context || this.muted) return;

        // Urgent beep when time is low
        this.playTone(440, 0.1, 'square', 0.25);
        setTimeout(() => {
            this.playTone(440, 0.1, 'square', 0.25);
        }, 150);
    }

    startBackgroundMusic(): void {
        if (!this.context || !this.masterGain || this.muted || this.musicPlaying) return;

        this.musicPlaying = true;

        // Simple looping melody using multiple oscillators
        const melody = [
            { freq: 523.25, duration: 0.25 }, // C5
            { freq: 659.25, duration: 0.25 }, // E5
            { freq: 783.99, duration: 0.25 }, // G5
            { freq: 659.25, duration: 0.25 }, // E5
            { freq: 523.25, duration: 0.25 }, // C5
            { freq: 392.00, duration: 0.25 }, // G4
            { freq: 523.25, duration: 0.5 },  // C5
        ];

        let currentTime = 0;
        const loopDuration = melody.reduce((acc, note) => acc + note.duration, 0);

        const playMelody = () => {
            if (!this.musicPlaying || !this.context || !this.masterGain) return;

            melody.forEach((note, i) => {
                const oscillator = this.context!.createOscillator();
                const gainNode = this.context!.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(this.masterGain!);

                oscillator.type = 'square';
                oscillator.frequency.value = note.freq;

                gainNode.gain.value = 0.05; // Very quiet background music

                const startTime = this.context!.currentTime + currentTime;
                oscillator.start(startTime);
                oscillator.stop(startTime + note.duration);

                currentTime += note.duration;
            });

            setTimeout(() => {
                currentTime = 0;
                playMelody();
            }, loopDuration * 1000);
        };

        playMelody();
    }

    stopBackgroundMusic(): void {
        this.musicPlaying = false;
    }

    toggleMute(): void {
        this.muted = !this.muted;
        if (this.muted) {
            this.stopBackgroundMusic();
        }
    }

    setVolume(volume: number): void {
        if (this.masterGain) {
            this.masterGain.gain.value = volume;
        }
    }

    isMuted(): boolean {
        return this.muted;
    }
}
